import { useDroppable } from '@dnd-kit/core';
import { formatInTimeZone } from 'date-fns-tz';
import { memo, useMemo } from 'react';

import { Tooltip } from '@/ui';
import { cn } from '@/ui/lib/utils';

import type { CalendarDroppableData } from '../../hooks/useCalendarDrag';
import { endOfDayInTimeZone, startOfDayInTimeZone } from '../../utils/dateGrid';
import EventBlock from '../EventBlock';

import type { MonthCellProps } from './types';

const MAX_VISIBLE_EVENTS = 3;

function MonthCell({
  day,
  events,
  timeZone,
  isCurrentMonth,
  isHighlighted = false,
  isToday,
  onEventClick,
  onSlotClick,
  onDayNumberClick,
  renderEventContent,
  renderEventPopover
}: MonthCellProps) {
  const dayStart = useMemo(() => startOfDayInTimeZone(day, timeZone), [day, timeZone]);
  const dayEnd = useMemo(() => endOfDayInTimeZone(day, timeZone), [day, timeZone]);

  const droppableData: CalendarDroppableData = { day: dayStart, wholeDay: true };
  const { setNodeRef } = useDroppable({
    id: `month-cell-${dayStart.toISOString()}`,
    data: droppableData
  });

  const dayEvents = useMemo(
    () =>
      events
        .filter((event) => !event.isBackground && event.start < dayEnd && event.end > dayStart)
        .sort((a, b) => a.start.getTime() - b.start.getTime()),
    [events, dayStart, dayEnd]
  );

  const visible = dayEvents.slice(0, MAX_VISIBLE_EVENTS);
  const overflow = dayEvents.slice(MAX_VISIBLE_EVENTS);

  return (
    <div
      ref={setNodeRef}
      onClick={(mouseEvent) => {
        if (mouseEvent.target !== mouseEvent.currentTarget) return;
        onSlotClick?.({ start: dayStart, end: dayEnd, isAllDay: true });
      }}
      className={cn(
        'flex min-h-24 cursor-pointer flex-col gap-0.5 border-b border-l border-uniform-gray-100 p-1 dark:border-uniform-gray-800',
        !isCurrentMonth && 'bg-uniform-gray-50/60 dark:bg-uniform-gray-900/40',
        isHighlighted && 'bg-yellow-50 dark:bg-yellow-900/20'
      )}>
      <span
        aria-current={isToday ? 'date' : undefined}
        onClick={(mouseEvent) => {
          mouseEvent.stopPropagation();
          onDayNumberClick?.(day);
        }}
        className={cn(
          'self-start rounded-full px-1.5 text-xs font-medium',
          onDayNumberClick && 'cursor-pointer hover:ring-2 hover:ring-uniform-gray-300',
          isToday && 'bg-green-600 text-white',
          !isToday && !isCurrentMonth && 'text-uniform-gray-400',
          !isToday && isCurrentMonth && 'text-uniform-gray-700 dark:text-uniform-gray-200'
        )}>
        {formatInTimeZone(day, timeZone, 'd')}
      </span>

      <div className="flex flex-col gap-0.5">
        {visible.map((event) => (
          <EventBlock
            key={event.id}
            event={event}
            timeZone={timeZone}
            compact
            onClick={onEventClick}
            renderEventContent={renderEventContent}
            renderEventPopover={renderEventPopover}
          />
        ))}
        {overflow.length > 0 && (
          <Tooltip
            interactive
            content={
              <div className="flex flex-col gap-1">
                {overflow.map((event) => (
                  <span key={event.id}>{event.title}</span>
                ))}
              </div>
            }>
            <span className="cursor-pointer text-[11px] font-medium text-uniform-gray-500 hover:underline dark:text-uniform-gray-400">
              +{overflow.length} more
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

export default memo(MonthCell);
